/**
 * Admin Messages Tab - Manage contact messages
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Send, Flag, Reply, Eye, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminMessagesTab = () => {
  const { messages, loadData } = useAdmin();
  
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [messageFilter, setMessageFilter] = useState("all");

  const filteredMessages = messages.filter(m => {
    if (messageFilter === "unread") return !m.read;
    if (messageFilter === "flagged") return m.flagged;
    if (messageFilter === "replied") return m.replied_at;
    return true;
  });

  const handleToggleRead = async (msg) => {
    try {
      await axios.put(`${API}/contact/messages/${msg.message_id}/read`, { read: !msg.read }, { withCredentials: true });
      loadData();
    } catch { toast.error("Failed to update"); }
  };

  const handleToggleFlag = async (msg) => {
    try {
      await axios.put(`${API}/contact/messages/${msg.message_id}/flag`, { flagged: !msg.flagged }, { withCredentials: true });
      loadData();
    } catch { toast.error("Failed to update"); }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/contact/messages/${msgId}`, { withCredentials: true });
      toast.success("Message deleted");
      loadData();
    } catch { toast.error("Delete failed"); }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !replyingTo) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/contact/messages/${replyingTo.message_id}/reply`, { reply_text: replyText }, { withCredentials: true });
      toast.success("Reply sent!");
      setReplyingTo(null);
      setReplyText("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle><Send className="w-5 h-5 inline mr-2" />Messages</CardTitle>
            <CardDescription>View and respond to contact messages</CardDescription>
          </div>
          <Select value={messageFilter} onValueChange={setMessageFilter}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {filteredMessages.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No messages</p>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map(msg => (
                <div key={msg.message_id} className={`p-4 rounded-lg border ${msg.read ? 'bg-white' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-stone-900">{msg.name}</p>
                      <p className="text-sm text-stone-600">{msg.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {msg.flagged && <Badge variant="destructive">Flagged</Badge>}
                      {msg.replied_at && <Badge className="bg-green-100 text-green-700">Replied</Badge>}
                      <span className="text-xs text-stone-500">{new Date(msg.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <p className="font-medium text-stone-800 mb-1">{msg.subject}</p>
                  <p className="text-stone-600 text-sm whitespace-pre-wrap">{msg.message}</p>
                  {msg.reply_text && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-700 font-medium mb-1">Your reply ({new Date(msg.replied_at).toLocaleDateString()}):</p>
                      <p className="text-sm text-green-800">{msg.reply_text}</p>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => handleToggleRead(msg)}>
                      <Eye className="w-4 h-4 mr-1" /> {msg.read ? "Unread" : "Read"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleToggleFlag(msg)}>
                      <Flag className={`w-4 h-4 mr-1 ${msg.flagged ? 'fill-red-500 text-red-500' : ''}`} /> {msg.flagged ? "Unflag" : "Flag"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReplyingTo(msg); setReplyText(""); }}>
                      <Reply className="w-4 h-4 mr-1" /> Reply
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteMessage(msg.message_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={!!replyingTo} onOpenChange={() => setReplyingTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reply to {replyingTo?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-stone-600 mb-2">Original: {replyingTo?.subject}</p>
            <p className="text-sm text-stone-500 mb-4 p-2 bg-stone-50 rounded">{replyingTo?.message}</p>
            <Textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyingTo(null)}>Cancel</Button>
            <Button onClick={handleSendReply} disabled={sendingReply || !replyText.trim()} className="bg-amber-600 hover:bg-amber-700">
              {sendingReply ? "Sending..." : "Send Reply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminMessagesTab;
